/**
 * Borrower-facing intent metadata for Owen apply question selection + wording.
 */
import { RECOMMENDATION_QUESTIONS } from './recommendationQuestions';

const JARGON = [
  'conforming', 'jumbo', 'agency', 'non-qm', 'nonqm', 'fhfa', 'baseline', 'high-balance',
  'super conforming', 'hfa', 'ami', 'ltv', 'dti', 'irrll', 'irrrl', 'nadl', 'mou',
  'fannie', 'freddie', 'refinow', 'first-lien', '203(k)', 'dscr', 'arm', 'w-2',
];

const CUSTOM_CATALOG = {
  purchaseOrRefinance: {
    borrowerGoal: 'Learn whether they are buying or refinancing.',
    plainLanguageExample: 'Are you buying a home or refinancing the mortgage you have now?',
  },
  refiGoal: {
    borrowerGoal: 'Understand refinance goal in everyday terms (payment vs cash back).',
    neverAskIf: 'purchaseOrRefinance is purchase',
    plainLanguageExample: 'What do you mainly want from your refinance?',
    doNotMention: JARGON,
  },
  propertyLocation: {
    borrowerGoal: 'Property location for limits and program rules — ZIP or state/county.',
    plainLanguageExample: 'What ZIP code is the property in?',
    doNotMention: JARGON,
  },
  propertyCounty: {
    borrowerGoal: 'Disambiguate county when ZIP spans multiple counties.',
    plainLanguageExample: 'Which county is the home in?',
    doNotMention: JARGON,
  },
  propertyUse: {
    borrowerGoal: 'Whether they will live there, use as second home, or rent it.',
    plainLanguageExample: 'Will you live in the home, use it as a vacation home, or rent it out?',
  },
  estimatedValue: {
    borrowerGoal: 'Approximate home value or purchase price.',
    plainLanguageExample: 'About how much is the home worth?',
    doNotMention: JARGON,
  },
  downPayment: {
    borrowerGoal: 'Purchase: cash down at closing. Refinance: amount still owed on the mortgage.',
    plainLanguageExample: 'About how much do you still owe on your mortgage?',
    doNotMention: ['down payment when refinancing', ...JARGON],
  },
  creditScore: {
    borrowerGoal: 'Self-reported credit score estimate.',
    plainLanguageExample: 'What is your best estimate of your credit score?',
  },
  propertyType: {
    borrowerGoal: 'Kind of home (house, condo, etc.).',
    plainLanguageExample: 'What type of home is it?',
  },
  militaryService: {
    borrowerGoal: 'Whether they may qualify for veteran home loan benefits.',
    plainLanguageExample: 'Have you served in the U.S. military, or are you the spouse of someone who did?',
  },
  ruralProperty: {
    borrowerGoal: 'Whether the home is in a rural or small-town area.',
    plainLanguageExample: 'Is the home in a small town or rural area rather than a major city?',
    doNotMention: ['USDA', ...JARGON],
  },
  stateHfaParticipant: {
    borrowerGoal: 'Whether a state/local affordable housing program is already involved.',
    plainLanguageExample: 'Is a state or local affordable homeownership program already helping you with this loan?',
    doNotMention: ['HFA', 'first-lien', ...JARGON],
  },
  tribalHousingPath: {
    borrowerGoal: 'Whether a tribal or Native housing program applies.',
    plainLanguageExample: 'Are you using a special home loan program for tribal members?',
    doNotMention: ['NADL', 'Section 184', 'MOU', ...JARGON],
  },
  affordableProgramInterest: {
    borrowerGoal: 'Interest in income-based assistance programs.',
    plainLanguageExample: 'Would you like me to include programs for buyers with moderate incomes?',
    doNotMention: ['AMI', 'agency', ...JARGON],
  },
  firstTimeHomeBuyer: {
    borrowerGoal: 'First-time buyer status for assistance programs.',
    plainLanguageExample: 'Is this the first home you have owned in the last few years?',
  },
  renovationNeeds: {
    borrowerGoal: 'Whether they need repair money included in the loan.',
    plainLanguageExample: 'Do you need money for repairs or upgrades included in the loan?',
    doNotMention: ['203(k)', ...JARGON],
  },
  agencyLoanServicer: {
    borrowerGoal: 'Who backs their current loan (if they know).',
    plainLanguageExample: 'Do you know if your current loan is backed by Fannie Mae or Freddie Mac? (Many people do not.)',
    doNotMention: JARGON,
  },
  refiNowLowIncomeHint: {
    borrowerGoal: 'Rough income level vs area typical income for special refi programs.',
    plainLanguageExample: 'Is your household income around or below the typical income in your area?',
    doNotMention: ['AMI', 'RefiNow', ...JARGON],
  },
  employmentStatus: {
    borrowerGoal: 'How they earn money.',
    plainLanguageExample: 'What best describes how you earn income?',
  },
  incomeDocumentation: {
    borrowerGoal: 'How self-employed or non-W2 borrowers prove income.',
    plainLanguageExample: 'How would you show your income to a lender?',
    doNotMention: ['DSCR', 'bank statement', ...JARGON],
  },
  nonQmFlexInterest: {
    borrowerGoal: 'Whether to include specialty non-standard loan types.',
    plainLanguageExample: 'Should I include specialty loans for unusual situations?',
    doNotMention: ['non-QM', 'Alt-Flex', ...JARGON],
  },
  loanTermPreference: {
    borrowerGoal: 'How long they want to pay off the loan.',
    plainLanguageExample: 'How many years would you like to take to pay off the loan?',
    doNotMention: ['amortization', ...JARGON],
  },
  rateTypePreference: {
    borrowerGoal: 'Fixed vs adjustable rate preference.',
    plainLanguageExample: 'Do you want a rate that stays the same, or one that can change later?',
    doNotMention: ['ARM', ...JARGON],
  },
  planToKeepLoan: {
    borrowerGoal: 'How long they expect to keep an adjustable loan.',
    plainLanguageExample: 'How long do you expect to keep this home before selling or refinancing again?',
  },
  refinanceType: {
    borrowerGoal: 'Type of VA refinance if applicable.',
    plainLanguageExample: 'What are you trying to do with your VA loan?',
    doNotMention: ['IRRRL', ...JARGON],
  },
  annualIncome: {
    borrowerGoal: 'Gross annual income.',
    plainLanguageExample: 'What is your total yearly income before taxes?',
    doNotMention: ['DTI', ...JARGON],
  },
  monthlyDebts: {
    borrowerGoal: 'Monthly debt payments.',
    plainLanguageExample: 'About how much do you pay each month on debts like car loans, cards, and student loans?',
    doNotMention: ['DTI', ...JARGON],
  },
};

export function buildQuestionCatalogForAi(allowedNextSteps = []) {
  const ids = new Set(
    allowedNextSteps
      .filter((s) => s.type === 'question')
      .map((s) => s.id),
  );

  return [...ids].map((id) => {
    const def = RECOMMENDATION_QUESTIONS.find((q) => q.id === id);
    const custom = CUSTOM_CATALOG[id] || {};
    return {
      id,
      internalField: id === 'propertyLocation' ? 'propertyState,propertyCounty,propertyZip' : id,
      borrowerGoal: custom.borrowerGoal || def?.question || 'Gather information for program matching.',
      plainLanguageExample: custom.plainLanguageExample || def?.question,
      neverAskIf: custom.neverAskIf || null,
      doNotMention: custom.doNotMention || [],
    };
  });
}

export function sanitizeAiQuestionText(text, maxLen = 120) {
  if (!text || typeof text !== 'string') return null;
  const trimmed = text.trim().slice(0, maxLen);
  return trimmed.length >= 8 ? trimmed : null;
}
